import type { ComponentProps, KeyboardEvent, ReactNode } from 'react';
import type { SortType, TableProps } from 'rsuite';
import type { ContentTypeDefinition } from '../../lib/flotiq-client/types/api-response';
import type { DropdownOption } from '../Dropdown/DropdownOption/DropdownOption';
import type { CheckCellProps, DataGridCheckRow } from './CheckCell/CheckCell';
import type {
  HeaderCellDragState,
  HeaderCellDropFn,
} from './DraggableHeaderCell/DraggableHeaderCell';
import type { DataGridGetRowLinkOptionsFn } from './DroppableCell/DroppableCell';
import type {
  DataGridHeaderFilter,
  HeaderFilterProps,
} from './HeaderFilter/HeaderFilter';
import type { HeaderFilterUpdateFiltersFn } from './HeaderFilterDatasource/HeaderFilterDatasource';

import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Checkbox, Table } from 'rsuite';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { CloseIcon } from '../../images/shapes';

import CheckCell from './CheckCell/CheckCell';
import DraggableHeaderCell from './DraggableHeaderCell/DraggableHeaderCell';
import DroppableCell from './DroppableCell/DroppableCell';
import HeaderFilter from './HeaderFilter/HeaderFilter';

const { Column, HeaderCell } = Table;

export type DataGridSortColumnFn = (column?: string, type?: SortType) => void;

export type DataGridCheckRowsFn = (checked: (string | number)[]) => void;

export type DataGridColumnResizeFn = (
  columnWidth?: number,
  dataKey?: string,
) => void;

export interface DataGridColumnProps {
  accessor: string;
  label: ReactNode;
  render: (value: any, rowData: any) => ReactNode;
  sortable?: boolean;
  additionalClasses?: string;
  flexGrow?: number;
  width?: number;
  minWidth?: number;
  justify?: string;
  hide?: boolean;
  resizable?: boolean;
  fixed?: boolean | 'left' | 'right';
  align?: 'left' | 'center' | 'right';
  notDraggable?: boolean;
  removable?: boolean;
  filterInputType?: HeaderFilterProps['filterInputType'];
  filterInputOptions?: HeaderFilterProps['options'];
  filterDatasourceFetch?: HeaderFilterProps['filterDatasourceFetch'];
  filterValidation?: HeaderFilterProps['validation'];
  multiple?: HeaderFilterProps['multiple'];
  showByDefault?: boolean;
  baseWidth?: number;
}

export interface DataGridProps {
  /**
   * DataGrid data
   */
  data: any[];
  /**
   * Additional classes for button
   */
  additionalClasses?: string;
  /**
   * Array of column definitions
   */
  columns: DataGridColumnProps[];
  /**
   * Show/Hide select column
   */
  hasSelectColumn?: boolean;
  /**
   * Sorting callback
   */
  onSortColumn?: DataGridSortColumnFn;
  /**
   * Waiting for data state
   */
  isLoading?: boolean;
  /**
   * Initial column for sorting
   */
  sort?: string;
  /**
   * Initial sort type
   */
  sortOrder?: SortType;
  /**
   * Array of checked ids
   */
  checkedRows?: DataGridCheckRow[];
  /**
   * Function firing on checking row checkboxes (keys: array) => void
   */
  onCheckRows?: DataGridCheckRowsFn;
  /**
   * DataGrid Height
   */
  tableHeight?: number;
  /**
   * The height of the DataGrid will be automatically expanded according
   * to the number of data rows, and no vertical scroll bar will appear.
   */
  autoHeight?: boolean;
  /**
   * If table should be expanded to full height of the parent container
   */
  fillHeight?: boolean;
  /**
   * DataGrid row height
   */
  rowHeight?: number;
  /**
   * If header should be visible
   */
  showHeader?: boolean;
  /**
   * Header Height
   */
  headerHeight?: number;
  /**
   * DataGrid status bar under table
   */
  statusBar?: ReactNode;
  /**
   * DataGrid no data info text
   */
  noDataInfo?: ReactNode;
  /**
   * DataGrid loading data info text
   */
  loadingDataInfoText?: string;
  /**
   * Loading data icon
   */
  loadingIcon?: ReactNode;
  /**
   * Data grid testid
   */
  testId?: string;
  /**
   * Data grid resize handler
   */
  onResize?: DataGridColumnResizeFn;
  /**
   * Data grid get remove column button on header label.
   * On click will trigger onRemoveColumn handler that pass field 'accessor'
   */
  removableColumns?: boolean;
  /**
   * Data grid show/hide sort options in header column.
   */
  sortableColumns?: boolean;
  /**
   * Data grid remove column handler.
   */
  onRemoveColumn?: (accessor: string) => void;
  /**
   * If data grid has filters
   */
  hasFilters?: boolean;
  /**
   * Disabled header filters
   */
  disableFilters?: boolean;
  /**
   * On filter change callback
   */
  onFilter?: (filters: DataGridHeaderFilter) => void;
  /**
   * Current filters
   */
  filters?: DataGridHeaderFilter;
  /**
   * Objects content type
   */
  contentType?: ContentTypeDefinition | null;
  /**
   * Callback to generate link options for every cell
   */
  getRowLinkOptions?: DataGridGetRowLinkOptionsFn;
  /**
   * On drop callback
   */
  onDrop?: HeaderCellDropFn;
}

const DataGrid = ({
  columns,
  hasSelectColumn = false,
  isLoading = false,
  data = EMPTY_ARRAY,
  additionalClasses,
  onSortColumn,
  sort = '',
  sortOrder = 'asc',
  checkedRows = EMPTY_ARRAY,
  onCheckRows,
  tableHeight = 300,
  autoHeight = true,
  fillHeight = false,
  rowHeight,
  showHeader = true,
  headerHeight = 60,
  statusBar,
  noDataInfo,
  loadingDataInfoText = '',
  loadingIcon,
  onDrop,
  testId,
  onResize,
  hasFilters = false,
  disableFilters = false,
  onFilter,
  removableColumns = false,
  sortableColumns = true,
  onRemoveColumn,
  filters = EMPTY_OBJECT,
  contentType,
  getRowLinkOptions,
}: DataGridProps) => {
  const [gridLoading, setGridLoading] = useState(isLoading);
  const [extraOptions, setExtraOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    if (!isLoading) setTimeout(() => setGridLoading(isLoading), 50);
    else setGridLoading(isLoading);
  }, [isLoading]);

  const [checkedKeys, setCheckedKeys] = useState(checkedRows || []);

  useEffect(() => {
    if (Array.isArray(checkedRows)) setCheckedKeys(checkedRows);
  }, [checkedRows]);

  const [draggedCol, setDraggedCol] = useReducer<string, [HeaderCellDragState]>(
    (oldCol, { accessor, isDragging }) => {
      if (!isDragging && oldCol === accessor) return '';
      if (isDragging) return accessor;
      return oldCol;
    },
    '',
  );

  /**
   * Find common keys between data ids and checked keys
   */
  const dataCheckedKeys = useMemo(() => {
    const checkedDictionary = checkedKeys.reduce(
      (acc, id) => {
        acc[id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    return data.map((row) => row.id).filter((id) => checkedDictionary[id]);
  }, [data, checkedKeys]);

  const checked = useMemo(
    () => dataCheckedKeys.length === data.length,
    [data, dataCheckedKeys],
  );

  const indeterminate = useMemo(
    () => dataCheckedKeys.length > 0 && dataCheckedKeys.length < data.length,
    [data, dataCheckedKeys],
  );

  const handleMultiSelect = useCallback(
    (value: string | number) => {
      const lasKey = checkedKeys[checkedKeys.length - 1];
      let keys: (string | number)[];

      if (!lasKey) {
        keys = [...checkedKeys, value];
      } else {
        const indexes: number[] = [];

        data.forEach((element, key) => {
          if ([lasKey, value].includes(element.id)) indexes.push(key);
        });

        keys = [
          ...checkedKeys,
          ...data
            .slice(indexes[0], indexes[1] + 1)
            .map((element) => element.id),
        ];
      }
      keys = [...new Set(keys)];
      setCheckedKeys(keys);
      onCheckRows?.(keys);
    },
    [checkedKeys, data, onCheckRows],
  );

  const handleCheckAll = useCallback<
    Exclude<ComponentProps<typeof Checkbox>['onChange'], undefined>
  >(
    (_, isChecked) => {
      const keys = isChecked
        ? [...new Set([...checkedKeys, ...data.map((item) => item.id)])]
        : checkedKeys.filter(
            (checked) => data.findIndex((item) => item.id === checked) < 0,
          );
      setCheckedKeys(keys);
      onCheckRows?.(keys);
    },
    [data, onCheckRows, checkedKeys],
  );

  const handleCheck = useCallback<
    Exclude<CheckCellProps['onChange'], undefined>
  >(
    (value, isChecked, event) => {
      if (
        isChecked &&
        (event.nativeEvent as unknown as KeyboardEvent<HTMLInputElement>)
          .shiftKey
      ) {
        handleMultiSelect(value as string);
        return;
      }

      let keys: (string | number)[];
      if (isChecked) {
        keys = value !== undefined ? [...checkedKeys, value] : checkedKeys;
      } else {
        keys = checkedKeys.filter((item) => item !== value);
      }
      setCheckedKeys(keys);
      onCheckRows?.(keys);
    },
    [checkedKeys, handleMultiSelect, onCheckRows],
  );

  const [currentSort, setCurrentSort] = useState<string | undefined>(sort);
  const [currentOrder, setCurrentOrder] = useState<SortType | undefined>(
    sortOrder,
  );
  const [currentFilters, setCurrentFilters] = useState(filters);

  useEffect(() => {
    setCurrentFilters(filters);
  }, [filters]);

  const handleSortColumn = useCallback<
    Exclude<TableProps['onSortColumn'], undefined>
  >(
    (column, type) => {
      let newSortType = type;
      let newSortColumn: string | undefined = column;

      if (currentSort === column) {
        if (currentOrder === 'asc') {
          newSortType = 'desc';
        } else if (currentOrder === 'desc') {
          newSortType = newSortColumn = undefined;
        }
      } else {
        newSortType = 'asc';
      }

      setCurrentSort(newSortColumn);
      setCurrentOrder(newSortType);
      onSortColumn?.(newSortColumn, newSortType);
    },
    [onSortColumn, currentSort, currentOrder],
  );

  useEffect(() => {
    setCurrentSort(sort);
    setCurrentOrder(sortOrder);
  }, [sort, sortOrder]);

  const handleFilters = useCallback<HeaderFilterUpdateFiltersFn>(
    (id, value, filterType, label) => {
      setCurrentFilters((prevState) => {
        const updatedFilter = { ...prevState };

        const isArray = Array.isArray(value);
        if ((isArray && value.length > 0) || (!isArray && value)) {
          updatedFilter[id] = { type: filterType, filter: value };
        } else {
          delete updatedFilter[id];
        }

        if (label) {
          setExtraOptions([{ value, label }]);
        }

        onFilter?.(updatedFilter);
        return updatedFilter;
      });
    },
    [onFilter],
  );

  return (
    <>
      <Table
        className={twMerge(
          'w-full font-semibold',
          'text-slate-900 dark:text-slate-200 border border-slate-100 dark:border-slate-800 rounded-t-lg',
          statusBar ? 'rounded-b-none' : 'rounded-b-lg',
          additionalClasses,
        )}
        showHeader={showHeader}
        height={tableHeight}
        fillHeight={fillHeight}
        autoHeight={autoHeight}
        rowHeight={rowHeight}
        headerHeight={hasFilters ? headerHeight + 60 : headerHeight}
        wordWrap={false}
        data={data}
        sortColumn={currentSort}
        sortType={currentOrder}
        onSortColumn={handleSortColumn}
        loading={gridLoading}
        renderEmpty={() => noDataInfo}
        renderLoading={() => (
          <div
            className={twMerge(
              'absolute flex items-center flex-col',
              'justify-center w-full h-full bg-white/90 dark:bg-slate-900/90 z-10',
            )}
            role="status"
          >
            {loadingIcon}
            <span className="mt-2">{loadingDataInfoText}</span>
          </div>
        )}
        {...getTestProps(testId, 'table')}
      >
        {hasSelectColumn && (
          <Column width={50} flexGrow={0} fixed="left">
            <HeaderCell className="cell-column">
              <div
                className={twMerge(
                  'flex h-full justify-center px-4 py-2',
                  !hasFilters && 'items-center',
                )}
              >
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                  disabled={isLoading || !data.length}
                  id="header-checkbox"
                  className={hasFilters ? 'header-checkbox-filters' : ''}
                  {...getTestProps(testId, 'header-checkbox')}
                />
                {hasFilters && <HeaderFilter />}
              </div>
            </HeaderCell>
            <CheckCell
              dataKey="id"
              checkedKeys={checkedKeys}
              onChange={handleCheck}
              className="cell-column"
            />
          </Column>
        )}

        {columns.map(
          (column, idx) =>
            !column.hide && (
              <Column
                key={column.accessor}
                fullText={true}
                width={column.width}
                minWidth={column.minWidth}
                sortable={sortableColumns ? column.sortable : false}
                {...(!column.resizable && {
                  flexGrow: column.flexGrow ?? 1,
                })}
                resizable={column.resizable}
                onResize={onResize}
                fixed={column.fixed}
                align={column.align}
                {...getTestProps(testId, `${idx}-column`)}
              >
                <DraggableHeaderCell
                  onDrop={onDrop}
                  accessor={column.accessor}
                  notDraggable={onDrop ? column.notDraggable : true}
                  testId={testId}
                  onDraggingStateChange={setDraggedCol}
                  hasFilters={hasFilters}
                >
                  {column.removable && removableColumns && (
                    <button
                      className={twMerge(
                        'w-4 h-4 flex justify-center shrink-0',
                        'items-center cursor-pointer',
                        'hover:bg-slate-100! rounded-full group',
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveColumn?.(column.accessor);
                      }}
                      {...getTestProps(
                        testId,
                        `remove-column-${column.accessor}`,
                      )}
                    >
                      <CloseIcon className="w-2 h-2 text-blue group-hover:text-black" />
                    </button>
                  )}

                  <div
                    className={twMerge('truncate', column.removable && 'pr-5')}
                  >
                    {column.label}
                  </div>
                  {hasFilters && (
                    <HeaderFilter
                      id={column.accessor}
                      filterInputType={column.filterInputType}
                      options={column.filterInputOptions}
                      handleFilters={handleFilters}
                      value={currentFilters?.[column.accessor]?.filter}
                      currentFilters={currentFilters}
                      dropdownLeftPosition={columns.length / 2 > idx}
                      validation={column.filterValidation}
                      extraOptions={extraOptions}
                      disabled={disableFilters}
                      contentType={contentType}
                      filterDatasourceFetch={column.filterDatasourceFetch}
                      multiple={column.multiple}
                      {...getTestProps(testId, `header-filter`, 'testId')}
                    />
                  )}
                </DraggableHeaderCell>
                <DroppableCell
                  className={twMerge(
                    'text-sm lg:text-base font-normal',
                    draggedCol === column.accessor
                      ? 'rs-dragged border-b-0!'
                      : '',
                  )}
                  dataKey={column.accessor}
                  accessor={column.accessor}
                  notDraggable={column.notDraggable}
                  onDrop={onDrop}
                  renderCell={column.render}
                  getRowLinkOptions={getRowLinkOptions}
                  testId={testId}
                />
              </Column>
            ),
        )}
      </Table>
      {statusBar && (
        <div {...getTestProps(testId, 'status-bar')}>{statusBar}</div>
      )}
    </>
  );
};

export default DataGrid;
